import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Typography } from "@mui/material";
import { VehicleContext } from "../../Context/vehicleContext";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DescriptionIcon from "@mui/icons-material/Description";
import BuildIcon from "@mui/icons-material/Build";
import SellIcon from "@mui/icons-material/Sell";
import CheckIcon from "@mui/icons-material/Check";
import VehicleInformationForm from "./GeneralInformation/VehicleInformationForm";
import { Vocabulary } from "../../Utils/Vocabulary";
import VehiclePhotoStep from "./GeneralInformation/VehiclePhotoStep";
import VehicleAdministrativeForm from "./GeneralInformation/VehicleAdministrativeForm";
import VehicleOptionsStep from "./GeneralInformation/VehicleOptionsStep";
import styles from "./styles/VehicleStepper.module.css";

export default function VehicleStepper() {
  const topRef = useRef<HTMLDivElement>(null);
  const vehicleContext = useContext(VehicleContext) as any;
  const vehicle = vehicleContext.state?.vehicle || vehicleContext.vehicle;

  const [searchParams, setSearchParams] = useSearchParams();
  const stepFromUrl = parseInt(searchParams.get("step") || "0", 10);
  const [activeStep, setActiveStep] = useState(stepFromUrl);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    // Initialize completed steps based on URL step
    const completed = new Set<number>();
    for (let i = 0; i < stepFromUrl; i++) {
      completed.add(i);
    }
    return completed;
  });

  // Sync URL with activeStep
  const updateUrl = useCallback(
    (step: number) => {
      setSearchParams({ step: step.toString() }, { replace: false });
    },
    [setSearchParams],
  );

  // Listen for browser back/forward navigation
  useEffect(() => {
    const stepParam = parseInt(searchParams.get("step") || "0", 10);
    if (stepParam !== activeStep && stepParam >= 0 && stepParam <= 3) {
      setActiveStep(stepParam);
    }
  }, [searchParams, activeStep]);

  // Guard: redirect to the earliest incomplete step when navigating via URL
  useEffect(() => {
    const hasVehicleData = !!(vehicle?.model_r?.id || vehicle?.model_id);

    if (activeStep >= 2 && !hasVehicleData) {
      const target = 1;
      setActiveStep(target);
      setSearchParams({ step: target.toString() }, { replace: true });
      return;
    }
  }, [activeStep, vehicle, setSearchParams]);

  // Scroll to top after step renders
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeStep]);

  const handleNext = () => {
    const nextStep = activeStep + 1;
    setCompletedSteps((prev) => new Set(prev).add(activeStep));
    setActiveStep(nextStep);
    updateUrl(nextStep);

  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    setActiveStep(prevStep);
    updateUrl(prevStep);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompletedSteps(new Set());
    updateUrl(0);
  };

  const steps = [
    {
      label: "Detectare vehicul",
      icon: DirectionsCarIcon,
      content: (
        <VehiclePhotoStep
          onNext={handleNext}
          onPrevious={handleBack}
          isFirstStep={activeStep === 0}
          isLastStep={false}
        />
      ),
    },
    {
      label: "Informatii vehicul",
      icon: DescriptionIcon,
      content: (
        <VehicleInformationForm
          onSubmit={handleNext}
          onPrevious={handleBack}
          isFirstStep={false}
          isLastStep={false}
        />
      ),
    },
    {
      label: Vocabulary.vehicleOptions,
      icon: BuildIcon,
      content: (
        <VehicleOptionsStep
          onNext={handleNext}
          onPrevious={handleBack}
          isFirstStep={false}
          isLastStep={false}
        />
      ),
    },
    {
      label: "Informatii vanzare",
      icon: SellIcon,
      content: (
        <VehicleAdministrativeForm
          onSubmit={handleNext}
          onPrevious={handleBack}
          isFirstStep={false}
          isLastStep={activeStep === 3}
        />
      ),
    },
  ];

  const getStepContent = (step: number) => {
    return steps[step].content;
  };

  const getConnectorProgress = () => {
    if (activeStep === 0) return 0;
    return (activeStep / (steps.length - 1)) * 100;
  };

  const getStepIconClass = (index: number) => {
    if (completedSteps.has(index)) {
      return `${styles.stepIconContainer} ${styles.stepIconCompleted}`;
    }
    if (index === activeStep) {
      return `${styles.stepIconContainer} ${styles.stepIconActive}`;
    }
    return `${styles.stepIconContainer} ${styles.stepIconInactive}`;
  };

  const getStepLabelClass = (index: number) => {
    if (completedSteps.has(index)) {
      return `${styles.stepLabel} ${styles.stepLabelCompleted}`;
    }
    if (index === activeStep) {
      return `${styles.stepLabel} ${styles.stepLabelActive}`;
    }
    return `${styles.stepLabel} ${styles.stepLabelInactive}`;
  };

  const renderStepIcon = (step: (typeof steps)[0], index: number) => {
    const IconComponent = step.icon;
    const isActive = index === activeStep;
    const isCompleted = completedSteps.has(index);

    if (isCompleted) {
      return (
        <CheckIcon
          className={`${styles.stepIcon} ${styles.stepIconActiveColor}`}
        />
      );
    }

    return (
      <IconComponent
        className={`${styles.stepIcon} ${
          isActive ? styles.stepIconActiveColor : styles.stepIconInactiveColor
        }`}
      />
    );
  };

  return (
    <div ref={topRef} className={styles.stepperContainer}>
      <div className={styles.stepperPaper}>
        <div className={styles.stepperHeader}>
          <Typography sx={{ fontWeight: 700, color: '#1a202c', fontSize: '1.25rem', lineHeight: 1.3 }}>
            {Vocabulary.addNewVehicle}
          </Typography>
          <span className={styles.stepperCounter}>
            {activeStep + 1} / {steps.length}
          </span>
        </div>

        {/* Custom Stepper */}
        <div className={styles.stepper}>
          {/* Connector Line */}
          <div className={styles.stepperConnector}>
            <div
              className={styles.stepperConnectorProgress}
              style={{ width: `${getConnectorProgress()}%` }}
            />
          </div>

          {/* Steps */}
          {steps.map((step, index) => (
            <div key={step.label} className={styles.step}>
              <div className={getStepIconClass(index)}>
                {renderStepIcon(step, index)}
              </div>
              <Typography className={getStepLabelClass(index)}>
                {step.label}
              </Typography>
            </div>
          ))}
        </div>

        {/* Content Area */}
        <div className={styles.contentArea}>
          {activeStep === steps.length ? (
            <div className={styles.completionContainer}>
              <div className={styles.completionIcon}>
                <Typography className={styles.completionCheckmark}>
                  ✓
                </Typography>
              </div>
              <Typography variant="h5" className={styles.completionTitle}>
                Vehicul creat cu succes!
              </Typography>
              <Typography variant="body1" className={styles.completionMessage}>
                Toate pasii au fost completati cu succes.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleReset}
                className={styles.resetButton}
              >
                Creaza alt vehicul
              </Button>
            </div>
          ) : (
            <div className={styles.stepContent}>
              {getStepContent(activeStep)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
